import { CatalogSection } from '@/features/admin/CatalogSection'
import { CustomerOverview } from '@/features/admin/CustomerOverview'
import { DataImportSection } from '@/features/admin/DataImportSection'
import { EventRecoverySection } from '@/features/admin/EventRecoverySection'
import { MetaCapiSection } from '@/features/admin/MetaCapiSection'
import { SeedingSection } from '@/features/admin/SeedingSection'
import { Button } from '@/components/ui/Button'
import {
  useAdminGetCustomerDetail,
  useAdminGetDashboardAccessToken,
  getAdminGetCustomerDetailQueryKey,
} from '@/api/generated/v2-admin/v2-admin'
import type { DashboardAccessTokenResponse } from '@/api/schemas'
// 하위 컴포넌트가 기존 adminApi 타입을 참조하므로 캐스팅용 import
import type { CustomerDetailResponse } from '@/lib/adminApi'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowLeft,
  ExternalLink,
  Globe,
  GraduationCap,
} from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'

// 캠퍼스 수강생 상세 역링크용 (campus-sso/select 등과 동일 fallback 패턴)
const CAMPUS_URL =
  import.meta.env.VITE_CAMPUS_URL || 'https://dev.1bpluschool.com'

const tabIds = [
  'overview',
  'data-import',
  'meta-capi',
  'catalog',
  'seeding',
  'event-recovery',
] as const

type SearchParams = {
  tab?: (typeof tabIds)[number]
}

export const Route = createFileRoute('/admin/customers/$customerId')({
  component: AdminCustomerDetail,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const tab = search.tab as string | undefined
    if (tab && tabIds.includes(tab as (typeof tabIds)[number])) {
      return { tab: tab as (typeof tabIds)[number] }
    }
    return {}
  },
})

type TabId = (typeof tabIds)[number]

interface Tab {
  id: TabId
  label: string
  component: React.ComponentType<any>
}

const tabs: Tab[] = [
  { id: 'overview', label: '개요', component: CustomerOverview },
  { id: 'data-import', label: '구매 데이터', component: DataImportSection },
  { id: 'meta-capi', label: 'CAPI', component: MetaCapiSection },
  { id: 'catalog', label: '카탈로그', component: CatalogSection },
  { id: 'seeding', label: '체험단', component: SeedingSection },
  { id: 'event-recovery', label: '이벤트 복구', component: EventRecoverySection },
]

function AdminCustomerDetail() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { customerId } = Route.useParams()
  const { tab } = Route.useSearch()

  const [isOpeningDashboard, setIsOpeningDashboard] = useState(false)

  const activeTab: TabId = tab || 'overview'

  // Orval Query 훅: Shop 상세 조회
  const { data: customerRaw, isLoading, error: queryError } = useAdminGetCustomerDetail(customerId)
  // custom-instance가 data만 반환하므로 직접 캐스팅
  const customer = (customerRaw ?? null) as unknown as CustomerDetailResponse | null

  // Orval Mutation 훅: 대시보드 접근 토큰 발급
  const dashboardTokenMutation = useAdminGetDashboardAccessToken({
    mutation: {
      onSuccess: (response) => {
        // onSuccess는 HTTP 200일 때만 호출되므로 성공 타입으로 캐스팅
        const tokenData = response as unknown as DashboardAccessTokenResponse
        // 고객 대시보드 URL 구성 (현재 호스트 기준, 토큰 URL 인코딩)
        const baseUrl = window.location.origin
        const dashboardUrl = `${baseUrl}/?access_token=${encodeURIComponent(tokenData.access_token)}`
        // 새 탭에서 열기
        window.open(dashboardUrl, '_blank')
      },
      onError: () => {
        toast.error('대시보드를 열 수 없어요.')
      },
      onSettled: () => {
        setIsOpeningDashboard(false)
      },
    },
  })

  // 고객 대시보드 열기
  const handleOpenDashboard = () => {
    if (!customer) return
    setIsOpeningDashboard(true)
    dashboardTokenMutation.mutate({ shopId: customer.id })
  }

  const setActiveTab = (newTab: TabId) => {
    navigate({
      to: '/admin/customers/$customerId',
      params: { customerId },
      search: { tab: newTab },
      replace: true,
    })
  }

  // 하위 컴포넌트에서 customer 업데이트 시 쿼리 캐시 갱신
  const handleCustomerUpdate = (updatedCustomer: CustomerDetailResponse) => {
    queryClient.setQueryData(
      getAdminGetCustomerDetailQueryKey(customerId),
      (old: any) => old ? { ...old, data: updatedCustomer } : old,
    )
  }

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-8 bg-surface-high rounded w-64 mb-6"></div>
          <div className="space-y-6">
            <div className="bg-surface-high h-48 rounded-lg"></div>
            <div className="bg-surface-high h-64 rounded-lg"></div>
          </div>
        </div>
      </div>
    )
  }

  if (queryError || !customer) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <div className="text-error text-lg">
            {queryError ? '고객사 정보를 불러오는데 실패했어요.' : '고객사를 찾을 수 없어요.'}
          </div>
          <Button
            className="mt-4"
            onClick={() => navigate({ to: '/admin/customers' })}
          >
            고객사 목록으로 돌아가기
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header: breadcrumb + 뒤로가기 개선 */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-on-surface">
            {customer.shop_name || `샵 #${customer.shop_no}`}
          </h1>
          <p className="text-sm text-on-surface-variant">
            {customer.cafe24_mall_id} · Shop #{customer.shop_no}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate({ to: '/admin/customers' })}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            고객사 목록
          </Button>
          {customer.primary_domain && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`https://${customer.primary_domain}`, '_blank')}
              className="flex items-center gap-2"
            >
              <Globe className="h-4 w-4" />
              쇼핑몰 이동
            </Button>
          )}
          {customer.campus_user_id && (
            <Button
              variant="outline"
              size="sm"
              title={customer.campus_user_name || undefined}
              onClick={() =>
                window.open(
                  `${CAMPUS_URL}/admin/students/${customer.campus_user_id}`,
                  '_blank',
                )
              }
              className="flex items-center gap-2"
            >
              <GraduationCap className="h-4 w-4" />
              캠퍼스 수강생
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={handleOpenDashboard}
            disabled={isOpeningDashboard}
            className="flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" />
            대시보드 이동
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-on-surface-variant hover:text-on-surface hover:border-border'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="pb-8">
        {activeTab === 'overview' && (
          <CustomerOverview
            customer={customer}
            onTabChange={setActiveTab}
            onCustomerUpdate={handleCustomerUpdate}
          />
        )}
        {activeTab === 'data-import' && (
          <DataImportSection
            customer={customer}
            onCustomerUpdate={handleCustomerUpdate}
          />
        )}
        {activeTab === 'meta-capi' && (
          <MetaCapiSection
            customer={customer}
            onCustomerUpdate={handleCustomerUpdate}
          />
        )}
        {activeTab === 'catalog' && <CatalogSection customer={customer} />}
        {activeTab === 'seeding' && <SeedingSection customer={customer} />}
        {activeTab === 'event-recovery' && (
          <EventRecoverySection
            customer={customer}
            onCustomerUpdate={handleCustomerUpdate}
          />
        )}
      </div>
    </div>
  )
}
